import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Outlet, useOutletContext } from "react-router-dom";
import { Icon } from "@iconify/react";
import { useLiveQuery } from "dexie-react-hooks";
import { leads } from "../../common/db";
import { changeDate, safeJSONParse } from "../../common/functions";
import "./LeadDetailsModal.css";

const LeadDetailsModal = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [isClosing, setIsClosing] = useState(false);

  // Pull-down gesture state
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartY = useRef(0);
  const modalRef = useRef(null);

  const navigate = useNavigate();
  const parentContext = useOutletContext();

  // Parse URL (handle both /events/ and /Events/)
  const fetch_url = window.location.href;
  const eventsIndex = fetch_url.toLowerCase().indexOf("events/");
  const event_id = fetch_url.includes("view-leads")
    ? fetch_url.substring(
        eventsIndex + 7,
        fetch_url.lastIndexOf("/view-leads")
      )
    : fetch_url.substring(
        eventsIndex + 7,
        fetch_url.lastIndexOf("/scanDetails")
      );

  const fetch_url_search = window.location.search;
  const urlParams = new URLSearchParams(fetch_url_search);
  const bookingId = urlParams.get("bookingId");

  // Get lead data from IndexedDB
  const leadData = useLiveQuery(
    async () => await leads.where("id").equals(bookingId).toArray(),
    [],
    false
  );

  const onCloseModal = () => {
    if (isClosing) return; // Prevent double-trigger from rapid clicks
    setIsClosing(true);
    // Wait for animation to complete before navigating
    setTimeout(() => {
      setIsVisible(false);
      navigate(-1);
      if (parentContext?.closeScanModal) {
        parentContext?.closeScanModal();
      }
    }, 300); // Match animation duration
  };

  // Pull-down gesture handlers
  const handleTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    const modalRect = modalRef.current?.getBoundingClientRect();
    if (modalRect && touch.clientY - modalRect.top < 60) {
      dragStartY.current = touch.clientY;
      setIsDragging(true);
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    const deltaY = touch.clientY - dragStartY.current;
    if (deltaY > 0) {
      setDragY(deltaY);
    }
  }, [isDragging]);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
    if (dragY > 100) {
      onCloseModal();
    } else {
      setDragY(0);
    }
  }, [isDragging, dragY]);

  const handleAddComment = () => {
    if (fetch_url.includes("view-leads")) {
      navigate({
        pathname: `/events/${event_id}/view-leads/DetailsCardModal/EditCommentModal`,
        search: `?bookingId=${leadData[0]?.bookingId}`,
      });
    } else {
      navigate({
        pathname: `/events/${event_id}/scanDetails/DetailsCardModal/EditCommentModal`,
        search: `?bookingId=${leadData[0]?.bookingId}`,
      });
    }
  };

  // Get latest comment
  const getLatestComment = () => {
    const comments = leadData?.[0]?.comment;
    if (!comments || comments === "undefined" || comments.length === 0) {
      return null;
    }
    const latestComment = safeJSONParse(comments[comments.length - 1]);
    return latestComment?.comment || null;
  };

  // Check if a lead is invalid/unrecognized (scanned but not a valid attendee for this event)
  const isInvalidLead = (lead) => {
    if (!lead) return false;

    // Manually saved leads should never be considered invalid
    // They are intentionally added by the user, even if they don't have all attendee data
    if (lead._manualSave === true) {
      return false;
    }

    const name = lead.name?.trim() || "";
    const bookingId = lead.bookingId?.trim() || "";
    const emailId = lead.emailId?.trim() || "";
    const designation = lead.designation?.trim() || "";
    const organisation = lead.organisation?.trim() || "";
    const phoneNumber = lead.phoneNumber?.trim() || "";

    // If name equals bookingId (case-insensitive), it's likely just raw scanned data
    const nameMatchesBookingId = name.toLowerCase() === bookingId.toLowerCase();

    // Check if there's any real attendee data
    const hasRealAttendeeData =
      (designation && designation !== "null") ||
      (organisation && organisation !== "null") ||
      (phoneNumber && phoneNumber !== "null") ||
      (emailId && emailId !== "null" && emailId.toLowerCase() !== bookingId.toLowerCase());

    // Invalid if: name matches bookingId AND no real attendee data
    if (nameMatchesBookingId && !hasRealAttendeeData) {
      return true;
    }

    // Also invalid if: no name, no email, and bookingId doesn't look like a valid konfhub booking
    if (!name && !emailId && bookingId && !hasRealAttendeeData) {
      return true;
    }

    return false;
  };

  if (!isVisible || !leadData) return null;

  const lead = leadData[0];
  const isDuplicate = parentContext?.duplicateEntry;
  const isInvalid = isInvalidLead(lead);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`lead-details__backdrop ${isClosing ? 'lead-details__backdrop--closing' : ''}`}
        onClick={onCloseModal}
      />

      {/* Modal Wrapper (contains close button + modal) */}
      <div
        className={`lead-details-wrapper ${isClosing ? 'lead-details-wrapper--closing' : ''}`}
        style={{
          transform: dragY > 0 ? `translateY(${dragY}px)` : undefined,
          transition: isDragging ? 'none' : 'transform 0.3s ease-out',
        }}
      >
        {/* Floating Close Button */}
        <button
          className="lead-details__close-btn"
          onClick={onCloseModal}
          aria-label="Close"
        >
          <Icon icon="mdi:close" width="20" height="20" />
        </button>

        {/* Main Modal Content */}
        <div
          className="lead-details"
          ref={modalRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
        {/* Drag Handle */}
        <div className="lead-details__drag-handle">
          <div className="lead-details__drag-handle-bar" />
        </div>

        {/* Content */}
        <div className="lead-details__content">
          {/* Already Captured Badge */}
          {isDuplicate && (
            <div className="lead-details__captured-badge">
              <div className="lead-details__captured-icon">
                <Icon icon="ep:success-filled" width="32" height="32" />
              </div>
              <p className="lead-details__captured-text">Already Captured!</p>
            </div>
          )}

          {/* Profile Section */}
          <div className="lead-details__profile">
            <div className="lead-details__profile-info">
              <h1 className={`lead-details__name ${(() => {
                // Check if any of the values is an email
                const name = lead?.name || "";
                const bookingId = lead?.bookingId || "";
                const emailId = lead?.emailId || "";
                const displayValue = isInvalid ? (bookingId || name || "Unknown") : (name || bookingId || emailId || "Unknown");
                // If it's an email (contains @), add the email class
                return displayValue.includes("@") ? "lead-details__name--email" : "";
              })()}`}>
                {(() => {
                  // For invalid leads, show just the bookingId (raw scanned value)
                  const displayValue = isInvalid
                    ? (lead?.bookingId || lead?.name || "Unknown")
                    : (lead?.name || lead?.bookingId || lead?.emailId || "Unknown");
                  // If it's an email (contains @), convert to lowercase
                  if (displayValue && displayValue.includes("@")) {
                    return displayValue.toLowerCase();
                  }
                  return displayValue;
                })()}
              </h1>
            </div>
            {/* Only show designation and organisation for valid leads */}
            {!isInvalid && lead?.designation && lead?.designation !== "null" && (
              <span className="lead-details__designation-badge">
                {lead.designation}
              </span>
            )}
            {!isInvalid && lead?.organisation && lead?.organisation !== "null" && (
              <p className="lead-details__organisation">{lead.organisation}</p>
            )}
          </div>

          {/* Invalid Lead Warning Banner */}
          {isInvalid && (
            <div className="lead-details__invalid-banner">
              <Icon icon="mdi:help-circle-outline" width="20" height="20" />
              <span>This lead data isn't recognised for this event.</span>
            </div>
          )}

          {/* Details List */}
          <div className="lead-details__list">
            {/* Only show phone and email for valid leads */}
            {!isInvalid && lead?.phoneNumber && lead?.phoneNumber !== "null" && (
              <div className="lead-details__row">
                <span className="lead-details__label">Phone No</span>
                <span className="lead-details__value">{lead.phoneNumber}</span>
              </div>
            )}

            {!isInvalid && lead?.emailId && lead?.emailId !== "null" && (
              <div className="lead-details__row">
                <span className="lead-details__label">Email Address</span>
                <span className="lead-details__value">{lead.emailId}</span>
              </div>
            )}

            <div className="lead-details__row">
              <span className="lead-details__label">Booking ID</span>
              <span className="lead-details__value">
                {lead?.bookingId || parentContext?.result?.bookingId || "-"}
              </span>
            </div>

            {lead?.createdAt && (
              <div className="lead-details__row">
                <span className="lead-details__label">Captured At</span>
                <span className="lead-details__value">
                  {changeDate(lead.createdAt)}
                </span>
              </div>
            )}

            <div className="lead-details__row">
              <span className="lead-details__label">Captured By</span>
              <span className="lead-details__value">
                {lead?.scannedBy || parentContext?.result?.scannedBy || "-"}
              </span>
            </div>

            <div className="lead-details__row">
              <span className="lead-details__label">Comment</span>
              <span className="lead-details__value">
                {getLatestComment() || "-"}
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="lead-details__footer">
          <button
            className="lead-details__add-comment-btn"
            onClick={handleAddComment}
          >
            {getLatestComment() ? "Edit Comment" : "Add Comment"}
          </button>
        </div>
        </div>
      </div>

      {/* Outlet for nested modals */}
      <Outlet context={{ event_id: event_id }} />
    </>
  );
};

export default LeadDetailsModal;
